import React, { useState } from "react";
import {
    Text,
    mergeStyleSets,
    DefaultButton,
} from "@fluentui/react";
import { IGameEntry, ITeam } from "./LiveScoreboard";
import ScoresheetModal from "./ScoresheetModal";

interface IGameCardProps {
    gameKey: string;
    game: IGameEntry;
    teamDictionary: Record<string, ITeam>;
}

const styles = mergeStyleSets({
    gameCard: {
        backgroundColor: "#ffffff",
        border: "1px solid #e1e1e1",
        borderRadius: 8,
        padding: 16,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    phaseBracket: {
        fontSize: 11,
        fontWeight: 600,
        color: "#605e5c",
        marginBottom: 4,
        textTransform: "uppercase" as const,
    },
    gameRound: {
        fontSize: 12,
        fontWeight: 600,
        color: "#0078d4",
        marginBottom: 8,
        textTransform: "uppercase" as const,
    },
    teamRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 0",
    },
    teamLogo: {
        width: 28,
        height: 28,
        objectFit: "contain" as const,
        marginRight: 8,
        flexShrink: 0,
    },
    teamName: {
        fontSize: 18,
        fontWeight: 500,
        flex: 1,
    },
    score: {
        fontSize: 24,
        fontWeight: 700,
        minWidth: 60,
        textAlign: "right" as const,
    },
    winningScore: {
        color: "#107c10",
    },
    gameInfo: {
        fontSize: 12,
        color: "#666",
        marginTop: 8,
        textAlign: "center" as const,
    },
});

export default function GameCard({ gameKey, game, teamDictionary }: IGameCardProps) {
    const [showScoresheet, setShowScoresheet] = useState(false);
    const teamEntries = Object.entries(game.teams);
    const [teamAId, teamAScore] = teamEntries[0] ?? ["", { score: 0, bonusPoints: 0 }];
    const [teamBId, teamBScore] = teamEntries[1] ?? ["", { score: 0, bonusPoints: 0 }];

    const teamAName = teamDictionary[teamAId]?.name ?? teamAId;
    const teamBName = teamDictionary[teamBId]?.name ?? teamBId;
    const teamALogo = teamDictionary[teamAId]?.logoUrl;
    const teamBLogo = teamDictionary[teamBId]?.logoUrl;
    const teamAWinning = teamAScore.score > teamBScore.score;
    const teamBWinning = teamBScore.score > teamAScore.score;

    return (
        <div className={styles.gameCard}>
            <Text className={styles.gameRound}>
                {game.round}
                {game.bracket && game.phase ? ` · ${game.phase} Bracket ${game.bracket}` : ""}
                {game.room ? ` · Room ${game.room}` : ""}
            </Text>
            <div className={styles.teamRow}>
                {teamALogo && <img src={teamALogo} alt="" className={styles.teamLogo} />}
                <Text className={styles.teamName}>{teamAName}</Text>
                <Text className={`${styles.score} ${teamAWinning ? styles.winningScore : ""}`}>{teamAScore.score}</Text>
            </div>
            <div className={styles.teamRow}>
                {teamBLogo && <img src={teamBLogo} alt="" className={styles.teamLogo} />}
                <Text className={styles.teamName}>{teamBName}</Text>
                <Text className={`${styles.score} ${teamBWinning ? styles.winningScore : ""}`}>{teamBScore.score}</Text>
            </div>
            <Text className={styles.gameInfo}>
                {game.isFinished ? "Final" : `Cycle ${game.currentQuestion}`}
            </Text>
            <DefaultButton
                text="View Scoresheet"
                onClick={() => setShowScoresheet(true)}
                styles={{ root: { marginTop: 10, width: "100%" } }}
            />
            {showScoresheet && (
                <ScoresheetModal
                    gameKey={gameKey}
                    game={game}
                    teamDictionary={teamDictionary}
                    onDismiss={() => setShowScoresheet(false)}
                />
            )}
        </div>
    );
};